import * as Sentry from "@sentry/vue"
import type { App } from "vue"

/**
 * Initialize Sentry for error tracking
 * Only initializes in production with a valid DSN
 */
export function initSentry(app: App): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  // Only initialize in production with a valid DSN
  if (import.meta.env.PROD && dsn) {
    Sentry.init({
      app,
      dsn,
      environment: import.meta.env.MODE,
      // __APP_VERSION__ is defined from package.json in vite.config.ts. This
      // used to read VITE_APP_VERSION, which was never set anywhere, so every
      // event would have been tagged release "1.0.0" regardless of the build.
      release: typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "unknown",

      // Performance monitoring
      tracesSampleRate: 0.1, // 10% of transactions

      // Session replay: error-triggered only. This is a finance app — never
      // sample healthy sessions, even with text masked and media blocked.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,

      // Filter out common non-actionable errors
      ignoreErrors: [
        // Browser extensions
        "top.GLOBALS",
        "originalCreateNotification",
        "canvas.contentDocument",
        "MyApp_RemoveAllHighlights",
        "http://tt.teletrader.com",
        "jbridge isConnected",

        // Network errors that are expected
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",

        // Firebase auth errors that are user-facing
        "auth/popup-closed-by-user",
        "auth/cancelled-popup-request",
        "auth/network-request-failed",
      ],

      // Don't send PII
      beforeSend(event) {
        // Remove user email from events
        if (event.user) {
          delete event.user.email
          delete event.user.ip_address
        }
        return event
      },

      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
    })
  }
}

/**
 * Set the current user for Sentry context
 */
export function setSentryUser(userId: string | null): void {
  if (import.meta.env.PROD) {
    if (userId) {
      Sentry.setUser({ id: userId })
    } else {
      Sentry.setUser(null)
    }
  }
}

/**
 * Capture an exception manually
 */
export function captureError(error: Error, context?: Record<string, unknown>): void {
  if (import.meta.env.PROD) {
    if (context) {
      Sentry.setContext("additional", context)
    }
    Sentry.captureException(error)
  } else {
    console.error("[Sentry would capture]:", error, context)
  }
}

/**
 * Capture a message manually
 */
export function captureMessage(message: string, level: "info" | "warning" | "error" = "info"): void {
  if (import.meta.env.PROD) {
    Sentry.captureMessage(message, level)
  } else {
    console.log(`[Sentry would capture ${level}]:`, message)
  }
}

/**
 * Add breadcrumb for better error context
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, unknown>): void {
  if (import.meta.env.PROD) {
    Sentry.addBreadcrumb({
      message,
      category,
      data,
      level: "info",
    })
  }
}
